import React, { useState, useEffect } from 'react';
import { Bell, BellOff } from 'lucide-react';
import apiClient from '../utils/api';
import './PushNotificationPermission.css';

function PushNotificationPermission() {
  const [permission, setPermission] = useState('default');
  const [showPrompt, setShowPrompt] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    checkPermission();
  }, []);

  const checkPermission = () => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
      
      // Show prompt if not yet decided
      if (Notification.permission === 'default') {
        const dismissed = localStorage.getItem('push-prompt-dismissed');
        if (!dismissed) {
          // Show after 30 seconds on first visit
          setTimeout(() => setShowPrompt(true), 30000);
        }
      }
    }
  };

  const requestPermission = async () => {
    if (!('Notification' in window)) {
      alert('Ihr Browser unterstützt keine Push-Benachrichtigungen');
      return;
    }

    if (!('serviceWorker' in navigator)) {
      alert('Service Worker wird nicht unterstützt');
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      setPermission(permission);

      if (permission === 'granted') {
        // Subscribe to push
        await subscribeToPush();
        setShowPrompt(false);
      } else {
        setShowPrompt(false);
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      alert('Fehler beim Aktivieren von Benachrichtigungen');
    }
  };

  const subscribeToPush = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      
      // Get VAPID public key from server
      const { publicKey } = await apiClient.getVapidPublicKey();
      
      // Convert base64 string to Uint8Array
      const applicationServerKey = urlBase64ToUint8Array(publicKey);
      
      // Subscribe
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey
      });

      // Send subscription to server
      await apiClient.subscribeToPush({
        endpoint: subscription.endpoint,
        keys: {
          p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
          auth: arrayBufferToBase64(subscription.getKey('auth'))
        }
      });

      setIsSubscribed(true);
      console.log('✓ Subscribed to push notifications');
      
      // Enable notifications in settings
      await apiClient.updateNotificationSettings({ enabled: true });
      
    } catch (error) {
      console.error('Error subscribing to push:', error);
      alert('Fehler beim Registrieren für Push-Benachrichtigungen');
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('push-prompt-dismissed', Date.now());
  };

  // Helper functions
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const arrayBufferToBase64 = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  };

  if (!showPrompt || permission !== 'default') {
    return null;
  }

  return (
    <div className="push-permission-prompt" data-testid="push-permission-prompt">
      <div className="push-prompt-content">
        <div className="push-prompt-icon">
          <Bell size={28} />
        </div>
        <div className="push-prompt-text">
          <div className="push-prompt-title">Benachrichtigungen aktivieren</div>
          <div className="push-prompt-subtitle">
            Erhalten Sie Erinnerungen für Ihre täglichen Routinen und wichtige Updates
          </div>
        </div>
        <div className="push-prompt-actions">
          <button
            className="push-btn-enable"
            onClick={requestPermission}
            data-testid="push-enable-button"
          >
            <Bell size={18} />
            Aktivieren
          </button>
          <button
            className="push-btn-dismiss"
            onClick={handleDismiss}
            data-testid="push-dismiss-button"
          >
            Später
          </button>
        </div>
      </div>
    </div>
  );
}

export default PushNotificationPermission;
